import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ProductDetail, StowageRule } from '../../../types/ProductTypes';
import { SectionCard, SectionTitle } from '../../Section.styles';
import { useEditMode } from '../../../../context/EditModeContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import useGetStowageRules, { StowageRuleItem } from '../../../../api/useGetStowageRules';
import {
    StowageRulesTable,
    StowageRuleRow,
    StowageRuleLink,
    NoRulesMessage,
    NoticeBox,
    SubsectionTitle,
    EditSelect,
    RemoveRuleButton,
} from './ORCASection.styles';

interface ORCASectionProps {
    product: ProductDetail;
}

const ORCASection: React.FC<ORCASectionProps> = ({ product }) => {
    const { isInEditMode } = useEditMode();
    const { mergePendingChanges } = useProductEdit();
    const { stowageRules: allStowageRules, getStowageRules } = useGetStowageRules();

    const categoryStowageRules = product.category?.stowageRules || [];
    const productStowageRules = product.stowageRules || [];

    const [localCategoryRules, setLocalCategoryRules] = useState<StowageRule[]>([]);
    const [localProductRules, setLocalProductRules] = useState<StowageRule[]>([]);
    const [selectedCategoryRuleId, setSelectedCategoryRuleId] = useState('');
    const [selectedProductRuleId, setSelectedProductRuleId] = useState('');

    const showEditUI = isInEditMode;

    useEffect(() => {
        if (showEditUI) {
            setLocalCategoryRules(categoryStowageRules);
            setLocalProductRules(productStowageRules);
        }
    }, [showEditUI, categoryStowageRules, productStowageRules]);

    useEffect(() => {
        if (showEditUI) {
            getStowageRules();
        }
    }, [showEditUI, getStowageRules]);

    const currentCategoryRules = showEditUI ? localCategoryRules : categoryStowageRules;
    const currentProductRules = showEditUI ? localProductRules : productStowageRules;

    const selectableCategoryRules = useMemo(() => {
        const currentIds = new Set(currentCategoryRules.map((r) => r.id));
        return allStowageRules.filter((r: StowageRuleItem) => !currentIds.has(r.id));
    }, [allStowageRules, currentCategoryRules]);

    const selectableProductRules = useMemo(() => {
        const currentProductIds = new Set(currentProductRules.map((r) => r.id));
        const currentCategoryIds = new Set(currentCategoryRules.map((r) => r.id));
        return allStowageRules.filter((r: StowageRuleItem) => !currentProductIds.has(r.id) && !currentCategoryIds.has(r.id));
    }, [allStowageRules, currentProductRules, currentCategoryRules]);

    const handleAddCategoryRule = useCallback(
        (ruleId: string) => {
            if (!ruleId) return;
            const ruleToAdd = allStowageRules.find((r) => r.id.toString() === ruleId);
            if (!ruleToAdd) return;
            const updated = [...localCategoryRules, { id: ruleToAdd.id, name: ruleToAdd.name } as StowageRule];
            setLocalCategoryRules(updated);
            setSelectedCategoryRuleId('');
            if (mergePendingChanges) {
                mergePendingChanges({
                    category: {
                        ...product.category,
                        stowageRules: updated,
                    } as any,
                });
            }
        },
        [allStowageRules, localCategoryRules, mergePendingChanges, product.category]
    );

    const handleAddProductRule = useCallback(
        (ruleId: string) => {
            if (!ruleId) return;
            const ruleToAdd = allStowageRules.find((r) => r.id.toString() === ruleId);
            if (!ruleToAdd) return;
            const updated = [...localProductRules, { id: ruleToAdd.id, name: ruleToAdd.name } as StowageRule];
            setLocalProductRules(updated);
            setSelectedProductRuleId('');
            if (mergePendingChanges) {
                mergePendingChanges({ stowageRules: updated });
            }
        },
        [allStowageRules, localProductRules, mergePendingChanges]
    );

    const handleRemoveCategoryRule = useCallback(
        (ruleId: number) => {
            const updated = localCategoryRules.filter((r) => r.id !== ruleId);
            setLocalCategoryRules(updated);
            if (mergePendingChanges) {
                mergePendingChanges({
                    category: {
                        ...product.category,
                        stowageRules: updated,
                    } as any,
                });
            }
        },
        [localCategoryRules, mergePendingChanges, product.category]
    );

    const handleRemoveProductRule = useCallback(
        (ruleId: number) => {
            const updated = localProductRules.filter((r) => r.id !== ruleId);
            setLocalProductRules(updated);
            if (mergePendingChanges) {
                mergePendingChanges({ stowageRules: updated });
            }
        },
        [localProductRules, mergePendingChanges]
    );

    return (
        <SectionCard>
            <SectionTitle>ORCA</SectionTitle>

            {/* Stowage rules for product category */}
            <SubsectionTitle>Stowage rules for this product category:</SubsectionTitle>
            {currentCategoryRules.length === 0 && !showEditUI ? (
                <NoRulesMessage>None</NoRulesMessage>
            ) : (
                <StowageRulesTable>
                    <tbody>
                        {currentCategoryRules.map((rule) => (
                            <StowageRuleRow key={rule.id}>
                                <td>
                                    {showEditUI && (
                                        <RemoveRuleButton onClick={() => handleRemoveCategoryRule(rule.id)} title="Remove Stowage Rule">
                                            🗑️
                                        </RemoveRuleButton>
                                    )}
                                </td>
                                <td>
                                    <StowageRuleLink href={`#/stowage-rule-details/${rule.id}`}>
                                        {rule.name} ({rule.id})
                                    </StowageRuleLink>
                                </td>
                            </StowageRuleRow>
                        ))}
                    </tbody>
                    {showEditUI && selectableCategoryRules.length > 0 && (
                        <tfoot>
                            <tr>
                                <td />
                                <td>
                                    <EditSelect
                                        value={selectedCategoryRuleId}
                                        onChange={(e) => {
                                            setSelectedCategoryRuleId(e.target.value);
                                            handleAddCategoryRule(e.target.value);
                                        }}
                                    >
                                        <option value="">Add stowage rule...</option>
                                        {selectableCategoryRules
                                            .slice()
                                            .sort((a, b) => a.name.localeCompare(b.name))
                                            .map((rule) => (
                                                <option key={rule.id} value={rule.id.toString()}>
                                                    {rule.name} ({rule.id})
                                                </option>
                                            ))}
                                    </EditSelect>
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </StowageRulesTable>
            )}

            {/* Stowage rules for charterer specific product/trade name */}
            <SubsectionTitle>Stowage rules for this charterer specific product/trade name:</SubsectionTitle>
            {currentProductRules.length === 0 && !showEditUI ? (
                <NoRulesMessage>None</NoRulesMessage>
            ) : (
                <StowageRulesTable>
                    <tbody>
                        {currentProductRules.map((rule) => (
                            <StowageRuleRow key={rule.id}>
                                <td>
                                    {showEditUI && (
                                        <RemoveRuleButton onClick={() => handleRemoveProductRule(rule.id)} title="Remove Stowage Rule">
                                            🗑️
                                        </RemoveRuleButton>
                                    )}
                                </td>
                                <td>
                                    <StowageRuleLink href={`#/stowage-rule-details/${rule.id}`}>
                                        {rule.name} ({rule.id})
                                    </StowageRuleLink>
                                </td>
                            </StowageRuleRow>
                        ))}
                    </tbody>
                    {showEditUI && selectableProductRules.length > 0 && (
                        <tfoot>
                            <tr>
                                <td />
                                <td>
                                    <EditSelect
                                        value={selectedProductRuleId}
                                        onChange={(e) => {
                                            setSelectedProductRuleId(e.target.value);
                                            handleAddProductRule(e.target.value);
                                        }}
                                    >
                                        <option value="">Add stowage rule...</option>
                                        {selectableProductRules
                                            .slice()
                                            .sort((a, b) => a.name.localeCompare(b.name))
                                            .map((rule) => (
                                                <option key={rule.id} value={rule.id.toString()}>
                                                    {rule.name} ({rule.id})
                                                </option>
                                            ))}
                                    </EditSelect>
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </StowageRulesTable>
            )}

            {/* Notice about hardcoded rules */}
            <NoticeBox>
                <strong>Notice:</strong> Other (hardcoded) rules are also tested in ORCA. Those rules are not visible in this overview.
            </NoticeBox>
        </SectionCard>
    );
};

export default ORCASection;
